import { useCallback } from 'react'
import { Menu } from '@highlight/ui'
import { FEATURE_FLAGS } from '@highlight/analytics'
import { useFlags } from '@highlight/analytics/react'
import { GlobalEvents } from '@highlight/ipc'
import {
  CalendarCheck,
  CircleHelp,
  Copy,
  CopyCheck,
  HeartPulse,
  Lightbulb,
  Logs,
  MessageCircleMore,
  Users,
} from 'lucide-react'
import { useIsLoggedIn } from '@renderer/hooks/useIsLoggedIn'
import { useUser } from '@renderer/hooks/useUser'
import { usePylon } from '@renderer/providers/PylonProvider'
import { useCopyDebugInfo } from '@renderer/components/help-menu/use-copy-debug-info'
import { AmplitudeRenderer } from '@renderer/utils/amplitude.renderer'
import { useMiniNotched } from '@renderer/overlay/Mini/atoms'
import { isProfessionalEmail } from '@renderer/overlay/Mini/setup-call/schedule'
import { OPEN_SETUP_CALL_EVENT } from '@renderer/overlay/Mini/setup-call/use-setup-call-offer'
import { useTopViewDebugInfo } from '../../../hooks/use-top-view-debug-info'
import { useOpenFeedbackInSettings } from '../../../utils/use-open-feedback-in-settings'
import { useCanViewBraintrustLinks } from '../../braintrust-span-link'
import { BraintrustLinkMenuItem } from '../../copy-braintrust-link-menu-item'

export function SidepanelHelpMenu() {
  return (
    <Menu.SubmenuRoot>
      <Menu.SubmenuTrigger>
        <CircleHelp />
        <span>Help</span>
      </Menu.SubmenuTrigger>
      <Menu.Content>
        <HelpMenuItems />
      </Menu.Content>
    </Menu.SubmenuRoot>
  )
}

/** Mounts only while the submenu is open, so the open view's debug lookups run on demand. */
function HelpMenuItems() {
  const pylon = usePylon()
  const isLoggedIn = useIsLoggedIn()
  const handleGiveFeedback = useOpenFeedbackInSettings('sidepanel toolbar')
  const { isCopied, copyDebugInfo } = useCopyDebugInfo()
  const debugInfo = useTopViewDebugInfo()
  const canViewBraintrustLinks = useCanViewBraintrustLinks()
  const [isNotched] = useMiniNotched()
  const user = useUser()
  const flags = useFlags({ live: true })
  const isEmailAllowed =
    !flags.isEnabled(FEATURE_FLAGS.SETUP_CALL_PROFESSIONAL_EMAIL_ONLY, false) || isProfessionalEmail(user?.email)
  // Booking happens in the notch, so a Mini that isn't in the notch has nowhere to open it
  const canBookSetupCall =
    flags.isEnabled(FEATURE_FLAGS.SETUP_CALL_INVITE, false) && isLoggedIn && isNotched && isEmailAllowed

  const handleBookSetupCall = useCallback(() => {
    AmplitudeRenderer.trackEvent('Get Help', { action: 'Book a setup session', source: 'sidepanel toolbar' })
    GlobalEvents.emit(OPEN_SETUP_CALL_EVENT)
  }, [])

  const handleChatWithSupport = useCallback(() => {
    AmplitudeRenderer.trackEvent('Get Help', { action: 'Contact Support', source: 'sidepanel toolbar' })
    pylon.toggle()
  }, [pylon])

  const handleJoinDiscord = useCallback(() => {
    window.open('https://discord.gg/hlai', '_blank', 'noopener,noreferrer')
    AmplitudeRenderer.trackEvent('Get Help', { action: 'Join Discord' })
  }, [])

  const handleOpenStatus = useCallback(() => {
    window.open('https://status.highlightai.com', '_blank', 'noopener,noreferrer')
    AmplitudeRenderer.trackEvent('Get Help', { action: 'Status Page' })
  }, [])

  const handleCopyDebugId = useCallback(() => {
    copyDebugInfo(debugInfo)
    AmplitudeRenderer.trackEvent('Get Help', { action: 'Copy Debug ID' })
  }, [copyDebugInfo, debugInfo])

  const handleShareDebugLogs = useCallback(() => {
    window.ipc.exportDebugLogsZip()
    AmplitudeRenderer.trackEvent('Get Help', { action: 'Share Debug Logs' })
  }, [])

  return (
    <>
      {canBookSetupCall && (
        <Menu.Item onClick={handleBookSetupCall}>
          <CalendarCheck />
          <span>Book a setup session</span>
        </Menu.Item>
      )}
      <Menu.Item onClick={handleChatWithSupport}>
        <MessageCircleMore />
        <span>Chat with support</span>
      </Menu.Item>
      {isLoggedIn && (
        <Menu.Item onClick={handleGiveFeedback}>
          <Lightbulb />
          <span>Give feedback</span>
        </Menu.Item>
      )}
      <Menu.Item onClick={handleJoinDiscord}>
        <Users />
        <span>Join our Discord</span>
      </Menu.Item>

      <Menu.Separator />

      <Menu.Item onClick={handleOpenStatus}>
        <HeartPulse />
        <span>Status</span>
      </Menu.Item>

      <Menu.Separator />

      <Menu.Item onClick={handleCopyDebugId} closeOnClick={false}>
        {isCopied ? <CopyCheck className="text-green" /> : <Copy />}
        <span>{isCopied ? 'Copied debug ID' : 'Copy debug ID'}</span>
      </Menu.Item>
      <Menu.Item onClick={handleShareDebugLogs}>
        <Logs />
        <span>Share debug logs</span>
      </Menu.Item>
      {canViewBraintrustLinks && <BraintrustLinkMenuItem link={debugInfo.braintrustLink} />}
    </>
  )
}
